/** Форматирование денег и дат для экранов участника (Stage 6). Деньги — копейки, даты — ISO UTC + TZ города. */

import { monthShort } from './texts';

const rub = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 0,
});

export function formatMoney(kopecks: number): string {
  return rub.format(Math.round(kopecks / 100));
}

function toDate(value: Date | string): Date {
  return typeof value === 'string' ? new Date(value) : value;
}

/** Таймзона устройства — когда таймзона города неизвестна (приглашение до вступления в отряд). */
export function deviceTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * «вт, 8 сентября, 20:00» — единый формат даты сбора (ревью 2026-09-24). Собирается из частей:
 * новые ICU отдают «8 сентября в 20:00», и строка рвалась перед временем. Между числом и
 * месяцем — неразрывный пробел, перенос возможен только после запятой.
 */
export function formatDateTime(value: Date | string, timezone: string = deviceTimezone()): string {
  const parts = new Intl.DateTimeFormat('ru-RU', {
    timeZone: timezone,
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(toDate(value));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('weekday')}, ${part('day')}\u00a0${part('month')}, ${part('hour')}:${part('minute')}`;
}

/** «8 сентября» */
export function formatDate(value: Date | string, timezone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: timezone,
    day: 'numeric',
    month: 'long',
  }).format(toDate(value));
}

/** День и месяц в зоне: месяц числом 0–11 — для своих сокращений (Intl даёт «нояб.», «сент.»). */
export function dayMonthIn(value: Date | string, timezone: string): { day: number; month: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    day: 'numeric',
    month: 'numeric',
  }).formatToParts(toDate(value));
  const num = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '1');
  return { day: num('day'), month: num('month') - 1 };
}

/** «8 сен», «1 ноя» (для диапазонов); месяц тремя буквами (раунд персон 2) */
export function formatDateShort(value: Date | string, timezone: string): string {
  const { day, month } = dayMonthIn(value, timezone);
  return `${day}\u00a0${monthShort(month)}`;
}

/** «20:00» */
export function formatTime(value: Date | string, timezone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(toDate(value));
}

/** Календарная дата `YYYY-MM-DD` (сезон `startsOn/endsOn`) → «8 сен». */
export function formatIsoDateShort(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return `${d} ${monthShort(m - 1)}`;
}

/** Доля в проценты: 0.75 → «75 %». */
export function formatRatio(ratio: number): string {
  return `${Math.round(ratio * 100)} %`;
}
